import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = "/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("ladderbot_token"));
  const [loading, setLoading] = useState(true);
  const [subscriptionOk, setSubscriptionOk] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await Promise.race([
            axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
            new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 6000))
          ]);
          setUser(response.data);

          // Check subscription status (non-blocking)
          try {
            const subResp = await Promise.race([
              axios.get(`${API}/auth/whop/check-subscription`, { headers: { Authorization: `Bearer ${token}` } }),
              new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 5000))
            ]);
            setSubscriptionOk(subResp.data.has_access !== false);
          } catch {
            setSubscriptionOk(true);
          }
        } catch (error) {
          console.error("Auth init error:", error?.message);
          if (error?.message !== 'timeout') {
            localStorage.removeItem("ladderbot_token");
            setToken(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  // Periodic subscription check (every 5 min)
  useEffect(() => {
    if (!token || !user) return;
    const interval = setInterval(async () => {
      try {
        const resp = await axios.get(`${API}/auth/whop/check-subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.data.has_access === false) {
          setSubscriptionOk(false);
        }
      } catch { /* ignore */ }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, user]);

  const handleWhopToken = useCallback((newToken) => {
    localStorage.setItem("ladderbot_token", newToken);
    setToken(newToken);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("ladderbot_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, username) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, password, username 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("ladderbot_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const whopLogin = () => {
    window.location.href = `/api/auth/whop/authorize`;
  };

  const logout = () => {
    localStorage.removeItem("ladderbot_token");
    setToken(null);
    setUser(null);
    setSubscriptionOk(true);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    subscriptionOk,
    login,
    register,
    whopLogin,
    handleWhopToken,
    logout,
    getAuthHeader
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
